<template>
  <div id="OfferBulkEdit">
    <BulkEditor
      :columns="columns"
      :source="formattedOffers"
      :validate-row="validateRow"
      :loading="loading"
      :resource-name="{
        singular: $t('campaignEdit.bulkEdit.offer'),
        plural: $t('campaignEdit.bulkEdit.offers')  
      }"
      :allow-delete="true"
      :allow-copy="false"
      :allow-create="false"
      @view-row="viewRow"
      @commit="saveOfferChanges"
      @discard="onDiscard"
    >
      <template v-slot:col:valueType="{item}">
        {{item.label}}
      </template>
      <template v-slot:col:promoType="{item}">
        {{item.label}}
      </template>
      <template v-slot:col:isDisabled="{item}">
        {{item.label}}
      </template>
      <template v-slot:col:language="{item}">
        {{item.label}}
      </template>
      <template v-slot:col:notionalValue="{rawValue}">
        <Formatter type="currency" :currency="campaign.currency" :value="rawValue"/>
      </template>
      <template v-slot:col:minPurchase="{rawValue}">
        <Formatter v-if="rawValue >= 0" type="currency" :currency="campaign.currency" :value="rawValue"/>
        <template v-else>—</template>
      </template>
      <template v-slot:col:maxRewards="{value, rawValue}">
        {{rawValue > 0 ? value : $t('None') }}
      </template>

    </BulkEditor>
    <!-- <pre>Committed Changes: {{changes}}</pre> -->
    <!-- <pre>Offers: {{offers}}</pre> -->
    <!-- <pre>Currencies: {{currencies}}</pre> -->
    <!--<pre>Available Variants: {{this.availableVariants.map(v => ({
      label: v,
      value: v
    }))}}</pre> -->
    <!-- <pre>Segments: {{segments}}</pre> -->
    <!-- <pre>OfferMap: {{offerMap}}</pre> -->
    <!-- <pre>WidgetMap: {{widgetMap}}</pre> -->
    <!-- <pre>campaign: {{campaign}}</pre> -->
  </div>
</template>

<script>
// import OffersUtil from '../classes/utils/OffersUtil'

export default {
  name: 'OfferBulkEdit',
  props: {
    campaign: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      offers: [],
      changes: [],
      widgetInfos: [],
      languages: [],
      singleUseCouponsEnabled: false,
      maxConditionLength: 200,
      loading: true
    }
  },
  mounted() {
    // console.log(this.$route.query)
    const offerIds = this.$route.query.id || []
    Promise.all([
      this.api.getIncentivesByIds(offerIds, this.campaign.id),
      this.api.getWidgetInfos(),
      this.api.getLanguageList(),
      this.api.getCurrentAccountPrefs()
    ])
      .then((response) => {
        const [offers, widgetInfos, languages, prefs] = response
        this.offers = offers || []
        this.widgetInfos = widgetInfos
        this.languages = languages && languages.map(l => {
          return {
            value: l.value,
            label: this.$t(`language.${l.value}`)
          }
        })
        this.singleUseCouponsEnabled = prefs.singleUseCoupons
        this.maxConditionLength = prefs.extendedOfferTextLengths ? 500 : 200
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.loading = false
      })
  },
  computed: {
    columns() {
      const rv = [
        {
          key: 'valueType',
          label: this.$t('campaignEdit.bulkEdit.valueType'),
          type: 'list',
          options: [
            { label: this.$t('Percentage'), value: '%' },
            { label: this.$t('Fixed'), value: '$' },
            { label: this.$t('Free Shipping'), value: 's' },
            { label: this.$t('Custom'), value: 't' }
          ]
        },
        {
          key: 'value',
          label: this.$t('Discount Value'),
          type: 'text',
          // format: 'decimal',
          // min: 0
        },
        {
          key: 'notionalValue',
          label: this.$t('campaignEdit.offer.anticipatedRetailValue'),
          type: 'number',
          format: 'currency',
          currency: this.campaign.currency,
          min: 0
        },
        {
          key: 'displayText',
          label: this.$t('Display'),
          type: 'text'
        },
        {
          key: 'promoType',
          label: this.$t('campaignEdit.bulkEdit.discountCodeType'),
          type: 'list',
          options: [
            {label: this.$t('Fixed code'), value: 'code'},
            {label: this.$t('Single-use'), value: 'file'}
          ]
        },
        {
          key: 'promoCode',
          label: this.$t('Discount Code'),
          type: 'text'
        },
        {
          key: 'isDisabled',
          label: this.$t('Status'),
          type: 'list',
          options: [
            {label: this.$t('Enabled'), value: 0},
            {label: this.$t('Disabled'), value: 1}
          ]
        },
        {
          key: 'minPurchase',
          label: this.$t('campaignEdit.bulkEdit.minimumPurchase'),
          type: 'number',
          format: 'currency',
          currency: this.campaign.currency,
          min: 0
        },
        {
          key: 'minPurchaseText',
          label: this.$t('campaignEdit.bulkEdit.minPurchaseText'),
          type: 'text'
        },
        {
          key: 'maxRewards',
          label: this.$t('campaignEdit.offer.usageLimit'),
          type: 'number',
          format: 'integer',
          min: 0
        },
        {
          key: 'validFromDate',
          label: this.$t('campaignEdit.bulkEdit.validityStart'),
          type: 'date',
          format: 'dateLong',
          width: 'minmax(150px, 1fr)',
          withTime: true
        },
        {
          key: 'expiryDate',
          label: this.$t('campaignEdit.bulkEdit.validityEnd'),
          type: 'date',
          format: 'dateLong',
          width: 'minmax(150px, 1fr)',
          withTime: true
        },
        { 
          key: 'timezone',
          label: this.$t('Timezone'),
          type: 'timezone',
          width: 'minmax(150px, 1fr)'
        },
        {
          key: 'language',
          label: this.$t('Language'),
          type: 'list',
          options: this.languages
        },
        {
          key: 'variant',
          label: this.$t('Variant'),
          type: 'list',
          options: this.formattedVariants
        },
        {
          key: 'condition',
          label: this.$t('Additional Conditions'),
          type: 'text',
          noWrap: true,
          multiline: true,
          width: 'minmax(250px, 1fr)'
        }
      ]
      return rv
    },
    formattedOffers() {
      return this.offers.map(o => {
        return {
          ...o,
          variant: o.settings && o.settings.classes ? o.settings.classes : null,
        }
      })
    },
    availableVariants() {
      const variants = this.widgetInfos.reduce((list, info) => {
        if (info.variants.length) {
          list.push(...info.variants);
        }
        return list;
      }, []);
      return [...new Set(variants)]
    },
    formattedVariants() {
      const vs = this.availableVariants.map(v => {
        return {
          label: v,
          value: v
        }
      })
      vs.unshift({
        label: this.$t('(none)'),
        value: ''
      })
      return vs
    }
  },
  methods: {
    offersVisible(segment) {
      return segment.type !== 'exclude' &&
        segment.type !== 'display-none' &&
        segment.type !== 'ignore-user'
    },
    validateRow(row) {
      if (row.value === null || row.value.toString().trim() === '') {
        return {
          colKey: 'value',
          message: this.$t('campaignEdit.bulkEdit.valueError')
        }
      }
      if (row.valueType !== 't' && isNaN(row.value)) {
        return {
          colKey: 'value',
          message: this.$t('campaignEdit.bulkEdit.valueNumberError')
        }
      }
      if (row.valueType === '$' && row.notionalValue !== parseInt(row.value)) {
        return {
          colKey: 'notionalValue',
          message: this.$t('campaignEdit.bulkEdit.valueRetailError')
        }
      }
      if (row.notionalValue === null || row.notionalValue.toString().trim() === '') {
        return {
          colKey: 'notionalValue',
          message: this.$t('campaignEdit.bulkEdit.retailValueError')
        }
      }
      if (!this.singleUseCouponsEnabled && row.promoType === 'file') {
        return {
          colKey: 'promoType',
          message: this.$t('campaignEdit.bulkEdit.singleUseNotAllowedError')
        }
      }
      if (row.condition && row.condition.length > this.maxConditionLength) {
        return {
          colKey: 'condition',
          message: this.$t('campaignEdit.bulkEdit.conditionLengthError', { value: `${this.maxConditionLength}` })
        }
      }
      if (row.minPurchase !== -1 && row.minPurchaseText) {
        return {
          colKey: 'minPurchase',
          message: this.$t('campaignEdit.bulkEdit.minPurchaseError')
        }
      }
      if (row.variant) {
        const found = this.formattedVariants.find(v => v.value === row.variant)
        if (!found) {
          return {
            colKey: 'variant',
            message: this.$t('validation.variantError', { variant: `${row.variant}` })
          }
        }
      }
      return false
    },
    viewRow(id) {
      let routeData = this.$router.resolve(`/campaigns/${this.campaign.id}/offers/edit/${id}`);
      window.open(routeData.href, '_blank');
    },
    async saveOfferChanges(changes) {
      this.changes = changes
      // console.log(this.changes)
      this.loading = true
      if (await this.checkAllowDelete()) {
        this.processChanges()
        this.api.saveBulkIncentives(this.changes, this.campaign.id)
          .then(() => {
            this.$ui.toast({ title: this.$t('Saved') })
            this.$router.push(`/campaigns/${this.campaign.id}/offers/list`)
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.loading = false
          })
      } else {
        this.loading = false
      }
    },
    processChanges() {
      this.changes.forEach(c => {
        if (c.changes) {
          if (c.changes.minPurchase !== undefined && !c.changes.minPurchase) {
            c.changes.minPurchase = -1
          }
          if (c.changes.maxRewards !== undefined && !c.changes.maxRewards) {
            c.changes.maxRewards = -1
          }
          if (c.changes.variant !== undefined) {
            c.changes.settings = {
              ...c.source.settings,
              classes: c.changes.variant
            }
          }
        }
      })
    },
    async checkAllowDelete() {
      for (const c of this.changes) {
        if (c.state === 'delete') {
          const segments = await this.api.getSegments(this.campaign.id).catch(this.apiCatch)
          const segmentsAttachedToOffer = this.getSegmentIds(c.source, segments) 
          if (segmentsAttachedToOffer && segmentsAttachedToOffer.length) {
            this.$ui.alert({
              title: `${this.$t('Alert')} — #${c.id}`,
              content: this.$t('campaignEdit.offer.removeFromAllSegmentsWarning')
            })
            this.changes = []
            return false
          }
        }
      }
      return true
    },
    getSegmentIds(incentive, segments) {
      let segIds = []
      if (segments) {
        segments.forEach((seg) => {
          if (seg.offerIds) {
            seg.offerIds.forEach((oId) => {
              if (oId === incentive.id) {
                segIds.push(seg.id)
              } 
            })
          }
        })
      }
      return segIds
    },
    onDiscard() {
      this.$router.push(`/campaigns/${this.campaign.id}/offers/list`)
    },
    formatWidgets(widgets) {
      let w = widgets.filter(w => {
        return w.type !== 'Smart'
      })
      w.unshift({
        name: `- ${this.$t('Use Default')} -`,
        id: -1
      })
      return w
    }
  }
}

</script>

<style>
.pill {
  padding: 2px 5px;
  border-radius: 3px;
  background: #e1e1e2;
  white-space: nowrap;
  overflow: hidden;
}

.pill +.pill {
  margin-top: 3px;
}
</style>